import type { Condition } from './enums';
import type { TreeLatestRow, TreeRow, ZoneRow } from './types';

export interface TreeFeatureProps {
  tree_id: string;
  tag: string;
  status: string;
  zone_id: string | null;
  species_id: string | null;
  condition: Condition | null;
  survey_date: string | null;
  height_cm: number | null;
  diameter_basal_cm: number | null;
  gps_accuracy_m: number | null;
}

export type PointGeometry = { type: 'Point'; coordinates: [number, number] };
export type PolygonGeometry = { type: 'Polygon'; coordinates: number[][][] };

export interface FeatureCollection<P, G = PointGeometry | PolygonGeometry> {
  type: 'FeatureCollection';
  features: Array<{ type: 'Feature'; id?: string; geometry: G; properties: P }>;
}

/** Trees with a position as a point FeatureCollection, with their latest measurement as properties (export + map source). */
export function treesToGeoJson(trees: readonly TreeRow[], latest: ReadonlyMap<string, TreeLatestRow> = new Map()): FeatureCollection<TreeFeatureProps, PointGeometry> {
  return {
    type: 'FeatureCollection',
    features: trees
      .filter((t) => t.lat != null && t.lng != null)
      .map((t) => {
        const l = latest.get(t.id);
        return {
          type: 'Feature' as const,
          id: t.id,
          geometry: { type: 'Point' as const, coordinates: [t.lng as number, t.lat as number] as [number, number] },
          properties: {
            tree_id: t.id,
            tag: t.tag,
            status: t.status,
            zone_id: t.zone_id,
            species_id: t.species_id,
            condition: l?.condition ?? null,
            survey_date: l?.survey_date ?? null,
            height_cm: l?.height_cm ?? null,
            diameter_basal_cm: l?.diameter_basal_cm ?? null,
            gps_accuracy_m: t.gps_accuracy_m,
          },
        };
      }),
  };
}

export function zonesToGeoJson(zones: readonly ZoneRow[]): FeatureCollection<{ zone_id: string; label: string }, PolygonGeometry> {
  return {
    type: 'FeatureCollection',
    features: zones
      .filter((z) => z.geom_geojson)
      .map((z) => ({
        type: 'Feature' as const,
        id: z.id,
        geometry: z.geom_geojson as PolygonGeometry,
        properties: { zone_id: z.id, label: z.label },
      })),
  };
}

/** Bounding box [minLng, minLat, maxLng, maxLat] of a set of points, or null when none has a position. */
export function bbox(points: ReadonlyArray<{ lat: number | null; lng: number | null }>): [number, number, number, number] | null {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const p of points) {
    if (p.lat == null || p.lng == null) continue;
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
  }
  return Number.isFinite(minLng) ? [minLng, minLat, maxLng, maxLat] : null;
}
